import { Injectable, Pipe, PipeTransform } from '@angular/core';
import { CommonDataService } from 'src/sb-tools/typescript/services/common-data.service';
import { DateFilterTypeNamePipe } from './date-filter-type-name.pipe';
import { FilterParamNamePipe } from './filter-param-name.pipe';
import { FolderNamePipe } from './folder-name.pipe';
import { SentimentPipe } from './sentiment.pipe';
import { TicketSourcePipe } from '@sb-tools/typescript/pipes/ticket-source.pipe';
import { TicketStatusPipe } from './ticket-status.pipe';
import { TimeUnitNamePipe } from './time-unit-name.pipe';
import { AssignStatusPipe } from './assign-status.pipe';
import { TicketSourceSubtypePipe } from '@sb-tools/typescript/pipes/ticket-source-subtype.pipe';
import { getCategoryById } from '../../core/utils/get-category-by-id.util';
import { TicketAssignStatus } from '@sb-tools/typescript/enums/ticket-assign-status.enum';
import { UserGroupDTO } from '@sb-tools/typescript/models/dto/user-group.dto';

@Injectable({ providedIn: 'root' })
@Pipe({ name: 'filterTooltipText' })
export class FilterTooltipTextPipe implements PipeTransform {
    allFields = [];
    tags = [];
    userGroups: UserGroupDTO[];
    users = [];

    constructor(
        public commonDataService: CommonDataService,
        private assignStatusPipe: AssignStatusPipe,
        private dateFilterTypeNamePipe: DateFilterTypeNamePipe,
        private filterParamNamePipe: FilterParamNamePipe,
        private folderNamePipe: FolderNamePipe,
        private sentimentPipe: SentimentPipe,
        private ticketSourceSubtypePipe: TicketSourceSubtypePipe,
        private ticketSourcePipe: TicketSourcePipe,
        private ticketStatusPipe: TicketStatusPipe,
        private timeUnitNamePipe: TimeUnitNamePipe,
    ) {}

    public transform({ originalModel, processedModel }, tags, users, allFields, userGroups: UserGroupDTO[]): string {
        this.tags = tags;
        this.users = users;
        this.allFields = allFields;
        this.userGroups = userGroups;

        const tooltipData = this.getTooltipData(originalModel, processedModel.params, processedModel.isCurrentUser);

        if (Object.keys(tooltipData).length === 0) {
            return '';
        } else {
            return this.renderTooltipHTML(tooltipData);
        }
    }

    private renderTooltipHTML(data) {
        let tooltipText = '';
        const sortedTooltipData = this.getSortedTooltipData(data);

        for (const key in sortedTooltipData) {
            if (Object.prototype.hasOwnProperty.call(sortedTooltipData, key)) {
                tooltipText =
                    tooltipText +
                    '<div>' +
                    key +
                    (sortedTooltipData[key] === true ? '' : ' = ' + sortedTooltipData[key]) +
                    '</div>';
            }
        }
        return tooltipText;
    }

    private getSortedTooltipData(data) {
        const ordered = {};
        Object.keys(data)
            .sort()
            .forEach((key) => {
                ordered[key] = data[key];
            });
        return ordered;
    }

    private getTooltipData(originModel, params, isCurrentUser) {
        const model: any = {};

        if (originModel.name.length > 20) {
            model[this.filterParamNamePipe.transform('filterName')] = originModel.name;
        }

        if (!isCurrentUser) {
            model[this.filterParamNamePipe.transform('createdBy')] =
                originModel.creator.name || originModel.creator.email;
        }

        for (const param in params) {
            if (
                param !== 'creationDateFilterValue' &&
                param !== 'creationDateFilterUnit' &&
                param !== 'creationDateFilterType' &&
                param !== 'isAssigned'
            ) {
                const paramValue = this.getFilterParamValue(
                    param,
                    param === 'fields' ? originModel.fields : params[param],
                );
                if (paramValue) {
                    model[this.filterParamNamePipe.transform(param)] = paramValue;
                }
            } else if (
                param === 'creationDateFilterType' &&
                params.creationDateFilterValue &&
                params.creationDateFilterUnit
            ) {
                model[this.filterParamNamePipe.transform('creationDateFilterType')] =
                    this.getFilterCreationParam(params);
            } else if (param === 'isAssigned') {
                const isAssigned = params[param];

                if (!isAssigned || (isAssigned && !params.assignees && !params.assigneeUserGroupIds)) {
                    const assignStatus: TicketAssignStatus = isAssigned
                        ? TicketAssignStatus.assigned
                        : TicketAssignStatus.unassigned;
                    model[this.filterParamNamePipe.transform('isAssigned')] = this.getFilterParamValue(
                        param,
                        assignStatus,
                    );
                }
            }
        }

        return model;
    }

    private getFilterCreationParam({
        creationDateFilterType: type,
        creationDateFilterValue: value,
        creationDateFilterUnit: unit,
    }) {
        return (
            this.dateFilterTypeNamePipe.transform(type) +
            ' ' +
            value +
            ' ' +
            (this.timeUnitNamePipe.transform(unit) + (value > 1 ? 's' : '')).toLowerCase()
        );
    }

    private getFilterParamValue(paramName, paramValue) {
        switch (paramName) {
            case 'onlyMy':
                return paramValue;
            case 'categoryIds':
                return this.getCategoriesNames(paramValue);
            case 'folder':
                return this.folderNamePipe.transform(paramValue);
            case 'status':
                return this.ticketStatusPipe.transform(paramValue);
            case 'hasAttachments':
                return paramValue;
            case 'hasNoReplyFromTeam':
                return paramValue;
            case 'withoutCategory':
                return paramValue;
            case 'isAssignedToBot':
                return paramValue;
            case 'isOnlyHelperCategories':
                return paramValue;
            case 'assigneeUserGroupIds':
                return this.getUserGroupNames(paramValue);
            case 'sentiment':
                return this.sentimentPipe.transform(paramValue);
            case 'sourceSubtype':
                return this.ticketSourceSubtypePipe.transform(paramValue);
            case 'source':
                return this.ticketSourcePipe.transform(paramValue);
            case 'tagIds':
                return this.getTagNames(paramValue);
            case 'isAssigned':
                return this.assignStatusPipe.transform(paramValue);
            case 'assignees':
                return this.getUserNames(paramValue);
            case 'fields':
                return this.getFieldsNames(paramValue);
            default:
                return '';
        }
    }

    private getUserGroupNames(userGroupsIdsString: string): string {
        const userGroupIds = userGroupsIdsString.split(',');
        const filteredUserGroups = this.userGroups.filter((group) => userGroupIds.includes(String(group.id)));
        const groupNames = filteredUserGroups.map((group) => group.name);
        return groupNames.join(', ');
    }

    // todo: doesn't look clear, check it
    private getUserNames(userIdsString): string {
        if (userIdsString) {
            const userIds = userIdsString.split(',');

            return this.users
                .filter((user) => userIds.includes(user.id))
                .map((filteredUser) => filteredUser.name || filteredUser.email)
                .join(', ');
        }
    }

    // todo: doesn't look clear, check it
    private getTagNames(tagsIdString): string {
        if (tagsIdString) {
            const tagIds = tagsIdString.split(',');

            return this.tags
                .filter((tag) => tagIds.includes(tag.id.toString()))
                .map((filteredTag) => filteredTag.text)
                .join(', ');
        }
    }

    private getFieldsNames(filterFieldsList): string {
        const fieldsNamesList = [];

        filterFieldsList.forEach((filterField) => {
            const fieldById = this.allFields.find((field) => {
                return field.id === filterField.key;
            });
            if (fieldById) {
                fieldsNamesList.push(`${fieldById.name}: ${filterField.value}`);
            }
        });

        return fieldsNamesList.join(', ');
    }

    private getCategoriesNames(categoryIdsString): string {
        if (categoryIdsString) {
            const categoryIds = categoryIdsString.split(',');
            const categoriesNameList = [];

            categoryIds.forEach((id) => {
                const category = getCategoryById(this.commonDataService.categories.all, id);
                if (category) {
                    categoriesNameList.push(category.label);
                }
            });
            return categoriesNameList.join(', ');
        }
    }
}
